import type { Rule } from 'antd/es/form';

/**
 * Composable antd form rules. See .claude/FORMS.md § V.*. Compose with
 * `rules={[V.text(1, 100), V.custom(...)]}`.
 *
 * These are UX, not truth. The API's Zod schema in `@velora/contracts` is
 * authoritative — validators here give the user a helpful early message.
 */

function required(message?: string): Rule {
  return { required: true, message: message ?? 'Required' };
}

function text(min?: number, max?: number): Rule[] {
  const rules: Rule[] = [];
  if (typeof min === 'number') rules.push({ min, type: 'string', message: `At least ${min} character${min === 1 ? '' : 's'}` });
  if (typeof max === 'number') rules.push({ max, type: 'string', message: `At most ${max} characters` });
  return rules;
}

function email(): Rule {
  return { type: 'email', message: 'Enter a valid email' };
}

function phone(): Rule {
  return {
    pattern: /^\+\d{1,4}\s\d[\d\s]{4,18}$/,
    message: 'Enter a valid phone number',
  };
}

function money(min?: number, max?: number): Rule {
  return { type: 'number', min: min ?? 0, max, message: 'Enter a valid amount' };
}

function match(pattern: RegExp, message: string): Rule {
  return { pattern, message };
}

function oneOf<T extends string>(values: readonly T[], message?: string): Rule {
  return {
    validator: (_r, value) => {
      if (value === undefined || value === null || value === '') return Promise.resolve();
      if (values.includes(value)) return Promise.resolve();
      return Promise.reject(new Error(message ?? `Must be one of: ${values.join(', ')}`));
    },
  };
}

function custom(validator: (value: unknown) => Promise<void>): Rule {
  return { validator: (_r, value) => validator(value) };
}

export const V = { required, text, email, phone, money, match, oneOf, custom };
